/**
 * Étape 2 — les assureurs (chunk 9.AZ.1, rendue autonome en 9.BC.1).
 *
 * ## Ce que 9.BC.1 change
 *
 * L'étape constatait. Son état vide disait « Ajoutez les assureurs depuis le
 * dossier, puis revenez ici » — un écran de préparation qui renvoie ailleurs
 * pour préparer. Le conseiller sortait du parcours au milieu de l'étape 2, et
 * devait retrouver son chemin seul.
 *
 * Elle décide maintenant : ajouter, retirer, voir l'adresse retenue et sa langue,
 * et corriger une adresse pour CET envoi. Le dossier garde sa propre surface de
 * gestion — ce n'est pas un déplacement, c'est une seconde porte.
 *
 * ## Ce qui n'est PAS ici
 *
 * **Aucune règle de résolution.** L'écran n'a jamais à décider quelle adresse
 * utiliser : il envoie `insurer_code`, le serveur renvoie le destinataire
 * composé. Une copie de la règle côté web divergerait de celle qui envoie
 * réellement, et cette divergence-là serait invisible.
 *
 * Kollectivia n'envoie **pas** en copie conforme invisible. Chaque assureur
 * reçoit son propre courriel, ce qui rend possible le suivi, les statuts et les
 * relances par destinataire.
 */

import type { QuoteRequestRecipientRow } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useInsurerCatalog } from "../../../lib/queries/insurers";
import { InsurerEmailBlockNotice } from "../../projects/_parts/InsurerEmailBlockNotice";

import { Etape } from "./Etape";

type Props = {
  /**
   * Le rang de l'étape dans le parcours.
   *
   * Paramétré depuis 9.BK.3 : l'étape de l'effectif s'intercale quand le
   * dossier en porte un et **disparaît** sinon. Un numéro codé en dur
   * produirait une suite trouée — 1, 3, 4 — qui ferait chercher l'étape
   * absente. La page sait quelles étapes elle rend ; elle les numérote.
   */
  numero?: number;
  destinataires: readonly QuoteRequestRecipientRow[];
  modifiable: boolean;
  enCours: boolean;
  onAjouter: (insurerCode: string) => void;
  onRetirer: (recipientId: string) => void;
  onCorrigerAdresse: (recipientId: string, email: string) => void;
  onChangerLangue: (recipientId: string, language: "fr" | "en") => void;
};

export function EtapeAssureurs(props: Props) {
  const { t } = useTranslation();
  const { labelFor } = useInsurerCatalog();
  const joignables = props.destinataires.filter((d) => !!d.recipient_email);
  const manquants = props.destinataires.filter((d) => !d.recipient_email);

  return (
    <Etape
      numero={props.numero ?? 2}
      titre={t("prepare_send.insurers.title")}
      description={t("prepare_send.insurers.subtitle")}
      etat={
        props.destinataires.length === 0
          ? "empty"
          : manquants.length > 0 || props.destinataires.some((d) => d.email_block_reason)
            ? "stale"
            : joignables.length > 0
              ? "ready"
              : "empty"
      }
    >
      {props.destinataires
        .filter((d) => d.email_block_reason)
        .map((d) => (
          <div key={d.id} className="mb-3">
            <p className="text-sm font-medium text-slate-900">
              {labelFor(d.insurer_code)} — {d.recipient_email}
            </p>
            <InsurerEmailBlockNotice reason={d.email_block_reason} feedback={d.delivery_feedback} />
          </div>
        ))}
      {/* 9.BE.1 — UNE seule liste. Auparavant les destinataires choisis
          apparaissaient dans une liste, et le sélecteur d'ajout en dessous les
          répétait : le même assureur à deux endroits, dans deux états. Ici, la
          case dit l'appartenance et la ligne dit le reste. */}
      {props.modifiable ? (
        <ChoixAssureurs
          destinataires={props.destinataires}
          enCours={props.enCours}
          onAjouter={props.onAjouter}
          onRetirer={props.onRetirer}
          onCorrigerAdresse={props.onCorrigerAdresse}
          onChangerLangue={props.onChangerLangue}
        />
      ) : props.destinataires.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-center text-sm text-slate-600">
          {t("prepare_send.insurers.empty_body")}
        </p>
      ) : (
        // Demande partie : plus rien à cocher, on montre ce qui a été envoyé.
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {props.destinataires.map((d) => (
            <LigneAssureur
              key={d.id}
              destinataire={d}
              modifiable={false}
              enCours={props.enCours}
              onRetirer={props.onRetirer}
              onCorrigerAdresse={props.onCorrigerAdresse}
              onChangerLangue={props.onChangerLangue}
            />
          ))}
        </ul>
      )}

      {manquants.length > 0 && (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          {t("prepare_send.insurers.blocked_note", { count: manquants.length })}
        </p>
      )}

      <p className="mt-3 text-xs text-slate-500">{t("prepare_send.insurers.individual_note")}</p>
    </Etape>
  );
}

/**
 * Une ligne dit trois choses : l'assureur, l'adresse retenue, et **d'où elle
 * vient**. La provenance compte : une adresse ponctuelle ne se comporte pas comme
 * une adresse de registre, et confondre les deux fait croire qu'on a corrigé le
 * référentiel alors qu'on a corrigé un seul envoi.
 */
function LigneAssureur(props: {
  destinataire: QuoteRequestRecipientRow;
  modifiable: boolean;
  enCours: boolean;
  onRetirer: (recipientId: string) => void;
  onCorrigerAdresse: (recipientId: string, email: string) => void;
  onChangerLangue: (recipientId: string, language: "fr" | "en") => void;
}) {
  const { t } = useTranslation();
  const { labelFor } = useInsurerCatalog();
  const [edite, setEdite] = useState(false);
  const [saisie, setSaisie] = useState(props.destinataire.recipient_email ?? "");
  const d = props.destinataire;
  const champId = `adresse-${d.id}`;

  function valider() {
    const propre = saisie.trim();
    if (!propre) return;
    props.onCorrigerAdresse(d.id, propre);
    setEdite(false);
  }

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-900">
            {labelFor(d.insurer_code)}
          </span>
          <span className="block truncate text-xs text-slate-500">
            {d.recipient_email ? (
              <>
                {d.recipient_email}
                {" · "}
                {d.language === "en"
                  ? t("prepare_send.insurers.language_en")
                  : t("prepare_send.insurers.language_fr")}
              </>
            ) : (
              t("prepare_send.insurers.no_email")
            )}
          </span>
        </span>

        {!d.recipient_email && (
          <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-900">
            {t("prepare_send.insurers.unreachable")}
          </span>
        )}

        {props.modifiable && (
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={props.enCours}
              onClick={() => setEdite((v) => !v)}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40"
            >
              {d.recipient_email
                ? t("prepare_send.insurers.change_address")
                : t("prepare_send.insurers.add_address")}
            </button>
            <button
              type="button"
              disabled={props.enCours}
              onClick={() => props.onRetirer(d.id)}
              aria-label={t("prepare_send.insurers.remove_aria", {
                insurer: labelFor(d.insurer_code),
              })}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40"
            >
              {t("prepare_send.insurers.remove")}
            </button>
          </span>
        )}
      </div>

      {props.modifiable && (
        <LangueDestinataire
          destinataire={d}
          enCours={props.enCours}
          onChangerLangue={props.onChangerLangue}
        />
      )}
      {edite && (
        <div className="mt-3 rounded-lg bg-slate-50 px-3 py-3">
          <label htmlFor={champId} className="block text-xs font-medium text-slate-700">
            {t("prepare_send.insurers.address_label")}
          </label>
          <p className="mt-0.5 text-xs text-slate-500">{t("prepare_send.insurers.address_help")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              id={champId}
              type="email"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              placeholder={t("prepare_send.insurers.address_placeholder")}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
            <button
              type="button"
              disabled={props.enCours || saisie.trim() === ""}
              onClick={valider}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-3 py-2 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {t("prepare_send.insurers.address_save")}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * La langue retenue et la correction ponctuelle d'adresse — 9.BE.1.
 *
 * Extrait de `LigneAssureur` plutôt qu'ajouté comme mode « compact » : deux
 * densités d'une même ligne auraient produit un booléen qui décide de la mise en
 * page, et ce booléen aurait grossi au chunk suivant. Ici, un composant fait une
 * chose — dire d'où vient l'adresse et permettre de la changer pour CET envoi —
 * et deux surfaces l'utilisent.
 */
function LangueDestinataire({
  destinataire: d,
  enCours,
  onChangerLangue,
}: {
  destinataire: QuoteRequestRecipientRow;
  enCours: boolean;
  onChangerLangue: (id: string, language: "fr" | "en") => void;
}) {
  const { t } = useTranslation();
  const id = `recipient-language-${d.id}`;
  return (
    <div className="min-w-0 space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-slate-700">
        {t("prepare_send.insurers.language_label")}
      </label>
      <select
        data-guide-control
        id={id}
        name="recipient_language"
        value={d.language}
        disabled={enCours || d.status !== "pending"}
        onChange={(e) => onChangerLangue(d.id, e.target.value as "fr" | "en")}
        className="input min-h-11 w-full min-w-0 bg-white text-slate-900 sm:w-auto"
      >
        <option value="fr">{t("prepare_send.insurers.language_fr")}</option>
        <option value="en">{t("prepare_send.insurers.language_en")}</option>
      </select>
      <p className="text-xs text-slate-500">{t("prepare_send.insurers.language_help")}</p>
    </div>
  );
}

function AdresseDestinataire(props: {
  destinataire: QuoteRequestRecipientRow;
  enCours: boolean;
  onCorrigerAdresse: (recipientId: string, email: string) => void;
  onChangerLangue: (recipientId: string, language: "fr" | "en") => void;
}) {
  const { t } = useTranslation();
  const [edite, setEdite] = useState(false);
  const [saisie, setSaisie] = useState(props.destinataire.recipient_email ?? "");
  const d = props.destinataire;
  const champId = `adresse-choix-${d.id}`;

  function valider() {
    const propre = saisie.trim();
    if (!propre) return;
    props.onCorrigerAdresse(d.id, propre);
    setEdite(false);
  }

  return (
    <div className="pb-2 pl-8 pr-2">
      <div className="flex flex-wrap items-center gap-2">
        <LangueDestinataire
          destinataire={d}
          enCours={props.enCours}
          onChangerLangue={props.onChangerLangue}
        />
        <button
          type="button"
          disabled={props.enCours}
          onClick={() => setEdite((v) => !v)}
          className="focus-visible:ring-brand-500 touch-manipulation rounded px-1.5 py-1 text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40"
        >
          {d.recipient_email
            ? t("prepare_send.insurers.change_address")
            : t("prepare_send.insurers.add_address")}
        </button>
      </div>

      {edite && (
        <div className="mt-2 rounded-lg bg-slate-50 px-3 py-3">
          <label htmlFor={champId} className="block text-xs font-medium text-slate-700">
            {t("prepare_send.insurers.address_label")}
          </label>
          <p className="mt-0.5 text-xs text-slate-500">{t("prepare_send.insurers.address_help")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              id={champId}
              type="email"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              placeholder={t("prepare_send.insurers.address_placeholder")}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
            <button
              type="button"
              disabled={props.enCours || saisie.trim() === ""}
              onClick={valider}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-3 py-2 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {t("prepare_send.insurers.address_save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * La liste des assureurs du cabinet, à cocher — 9.BE.1.
 *
 * ## Ce que remplace cette liste
 *
 * Choisir dans un menu, cliquer « Ajouter », recommencer. Trois gestes par
 * assureur, pour un cabinet qui en a huit et qui envoie couramment la même
 * demande à plusieurs. Le bouton « Ajouter » a disparu : **cocher engage**.
 *
 * ## Pourquoi une case et pas un multi-select
 *
 * À cette échelle, un multi-select avec bouton de validation a la lourdeur d'un
 * formulaire pour ce qui devrait être un geste. Et surtout, une ligne peut
 * montrer **l'adresse résolue avant le choix** — aujourd'hui on découvre
 * « injoignable » après avoir ajouté.
 *
 * ## La correspondance avec le serveur est exacte
 *
 * Une coche = un `POST recipients`, une décoche = un `DELETE`. Aucun endpoint
 * de lot, aucune règle métier ici : l'adresse est résolue par le serveur, et
 * cet écran ne fait que refléter ce qu'il a décidé. La décoche est réversible,
 * et la liste disparaît dès que la demande quitte le brouillon.
 */
function ChoixAssureurs(props: {
  destinataires: readonly QuoteRequestRecipientRow[];
  enCours: boolean;
  onAjouter: (insurerCode: string) => void;
  onRetirer: (recipientId: string) => void;
  onCorrigerAdresse: (recipientId: string, email: string) => void;
  onChangerLangue: (recipientId: string, language: "fr" | "en") => void;
}) {
  const { t } = useTranslation();
  const { activeOptions, isLoading, isError } = useInsurerCatalog();

  const parCode = new Map(props.destinataires.map((d) => [d.insurer_code, d]));
  // Un destinataire dont l'assureur a été désactivé depuis reste sur la demande
  // et partira quand même : le cacher ferait croire qu'il n'existe pas.
  const codesActifs = new Set(activeOptions.map((o) => o.value));
  const horsCatalogue = props.destinataires.filter((d) => !codesActifs.has(d.insurer_code));

  if (isLoading) {
    return (
      <div className="mt-4" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t("common.insurers.loading")}</span>
        <div className="h-10 animate-pulse rounded-lg bg-slate-100 motion-reduce:animate-none" />
      </div>
    );
  }
  if (isError) {
    return (
      <p role="alert" className="mt-4 text-sm text-red-700">
        {t("common.insurers.error")}
      </p>
    );
  }
  if (activeOptions.length === 0) {
    return <p className="mt-4 text-sm text-slate-600">{t("common.insurers.empty")}</p>;
  }

  return (
    <fieldset className="mt-4" disabled={props.enCours}>
      <legend className="text-sm font-medium text-slate-800">
        {t("prepare_send.insurers.choose_label")}
      </legend>
      <p className="mt-0.5 text-xs text-slate-500">{t("prepare_send.insurers.choose_help")}</p>

      <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
        {activeOptions.map((o) => {
          const choisi = parCode.get(o.value);
          // Injoignable vaut AVANT et APRÈS la coche : non coché, c'est le
          // registre qui n'a rien ; coché, c'est le destinataire lui-même — et
          // c'est ce second cas qui bloquera l'envoi. Ne le signaler que sur les
          // lignes non cochées cacherait précisément celui qui compte.
          const injoignable = choisi ? !choisi.recipient_email : o.submissionEmail === null;
          return (
            <li key={o.value}>
              {/* Le label ENVELOPPE la case : toute la ligne est la cible, sans
                  zone morte entre le texte et la case à cocher. */}
              <label className="focus-within:ring-brand-500 flex cursor-pointer items-start gap-2 rounded-lg px-2 py-2 focus-within:ring-2 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={!!choisi}
                  disabled={props.enCours}
                  onChange={() => {
                    if (choisi) props.onRetirer(choisi.id);
                    else props.onAjouter(o.value);
                  }}
                  className="text-brand-600 focus-visible:ring-brand-500 mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-slate-900">{o.label}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {choisi?.recipient_email ??
                      o.submissionEmail ??
                      t("prepare_send.insurers.no_email")}
                  </span>
                </span>
                {injoignable && (
                  <span className="ml-auto shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                    {t("prepare_send.insurers.unreachable")}
                  </span>
                )}
              </label>

              {/* Coché : la ligne devient opérationnelle — langue retenue,
                  provenance de l'adresse, et correction ponctuelle. */}
              {choisi && (
                <AdresseDestinataire
                  destinataire={choisi}
                  enCours={props.enCours}
                  onCorrigerAdresse={props.onCorrigerAdresse}
                  onChangerLangue={props.onChangerLangue}
                />
              )}
            </li>
          );
        })}
      </ul>

      {horsCatalogue.length > 0 && (
        <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {horsCatalogue.map((d) => (
            <LigneAssureur
              key={d.id}
              destinataire={d}
              modifiable
              enCours={props.enCours}
              onRetirer={props.onRetirer}
              onCorrigerAdresse={props.onCorrigerAdresse}
              onChangerLangue={props.onChangerLangue}
            />
          ))}
        </ul>
      )}
    </fieldset>
  );
}
